/**
 * SHARED CLIENT FETCH — one request per URL, however many components ask for it.
 *
 * WHY THIS EXISTS (owner 2026-08-07, "stock market today is slow"). Measured in the browser, the page
 * made 27 API calls to 12 UNIQUE endpoints, 1,240ms of it duplicated. Two pairs were fired at the SAME
 * INSTANT by different components with no shared cache:
 *   /api/market/indices  — HeaderIndices is mounted TWICE in Navbar (desktop + mobile). A component
 *                          hidden with `hidden lg:block` STILL MOUNTS AND STILL FETCHES; CSS does not
 *                          stop an effect.
 *   /api/build-info      — <BuildVersion/> in app/layout.tsx and useBuildInfo() in Navbar.
 * ⭐ THESE ARE HEADER COMPONENTS ON EVERY PAGE, so the waste was site-wide, not specific to that page.
 *
 * WHAT IT DOES, and deliberately nothing more:
 *   1. DEDUPE IN FLIGHT — concurrent callers for the same URL share one promise. This is the fix for
 *      the simultaneous pairs, which no HTTP cache could ever have solved (build-info even carried a
 *      `?t=${Date.now()}` buster, guaranteeing two distinct cache keys).
 *   2. SHORT TTL — a later caller within `ttlMs` gets the cached value instead of a new request.
 *
 * ⚠ NOT a data-fetching library and must not grow into one. No revalidation, no retries, no
 * subscriptions — callers keep their own polling timers and error handling. The one job is "do not ask
 * twice for the thing we are already asking for".
 * ⚠ A FAILED REQUEST IS NOT CACHED. It is removed from both maps so the next caller retries; caching a
 * failure would turn one blip into a permanently empty header.
 */

type Entry = { at: number; data: unknown }

const inflight = new Map<string, Promise<unknown>>()
const cache = new Map<string, Entry>()

/**
 * Fetch `url` at most once per `ttlMs`, sharing the in-flight request across concurrent callers.
 * `key` lets callers with a cache-busted URL still share one entry.
 */
export async function sharedFetch<T>(url: string, ttlMs = 15_000, key = url): Promise<T> {
  const hit = cache.get(key)
  if (hit && Date.now() - hit.at < ttlMs) return hit.data as T

  const existing = inflight.get(key)
  if (existing) return existing as Promise<T>

  const p = fetch(url, { cache: 'no-store', headers: { 'Cache-Control': 'no-cache' } })
    .then((r) => {
      if (!r.ok) throw new Error(`${url} -> ${r.status}`)
      return r.json()
    })
    .then((data) => {
      cache.set(key, { at: Date.now(), data })
      return data as T
    })
    .finally(() => { inflight.delete(key) })

  inflight.set(key, p)
  return p
}

/** Drop a cached entry so the next call refetches. Used by pollers that want a guaranteed fresh read. */
export function invalidateShared(key: string): void {
  cache.delete(key)
}
